/**
 * Scenarios Simulator Enums
 *
 * Centralized enum definitions for the Scenarios Simulator feature.
 * All enum types are exported from this file to avoid duplication across the codebase.
 *
 * Convention:
 *   - Enum VALUE = English identifier (sent to/received from backend)
 *   - Label map  = Portuguese display text (used in selects and UI)
 */

// ============================================
// SIMULATOR TYPES
// ============================================

export enum SimulatorTypeEnum {
  SCENARIOS_SIMULATOR,
}

export enum ContractGeneratorTypeEnum {
  PRODUCT_ONE = 'PRODUCT_ONE',
  PRODUCT_TWO = 'PRODUCT_TWO',
}

export const ContractGeneratorTypeLabels: Record<ContractGeneratorTypeEnum, string> = {
  [ContractGeneratorTypeEnum.PRODUCT_ONE]: 'Clarke Gestão',
  [ContractGeneratorTypeEnum.PRODUCT_TWO]: 'Clarke Varejo',
};

// ============================================
// PRODUCT & MODALITY TYPES
// ============================================

/**
 * Product type enum for energy contracts
 * Values are English identifiers matching backend API
 */
export enum ProductTypeEnum {
  ACL_WHOLESALER = 'ACL_WHOLESALER',
  ACL_RETAILER = 'ACL_RETAILER',
  PRODUCT_2 = 'PRODUCT_2',
  PRODUCT_TWO = 'PRODUCT_TWO', // Alias for PRODUCT_2 to avoid confusion in codebase
}

export const ProductTypeLabels: Record<ProductTypeEnum, string> = {
  [ProductTypeEnum.ACL_WHOLESALER]: 'Gestão Atacadista',
  [ProductTypeEnum.ACL_RETAILER]: 'Gestão Varejista',
  [ProductTypeEnum.PRODUCT_2]: 'Clarke Varejo',
  [ProductTypeEnum.PRODUCT_TWO]: 'Clarke Varejo',
};

export enum ProductTypeSelectEnum {
  ACL_RETAILER = 'ACL_RETAILER',
  ACL_WHOLESALER = 'ACL_WHOLESALER',
}

export const ProductTypeSelectLabels: Record<ProductTypeSelectEnum, string> = {
  [ProductTypeSelectEnum.ACL_RETAILER]: 'Varejista',
  [ProductTypeSelectEnum.ACL_WHOLESALER]: 'Atacadista',
};

// ============================================
// COMPANY TYPES
// ============================================

export enum CompanyTypeEnum {
  FACTORY = 'FACTORY',
  GAS_STATION = 'GAS_STATION',
  RESTAURANT = 'RESTAURANT',
  OTHER = 'OTHER',
  MARBLE_FACTORY = 'MARBLE_FACTORY',
  HOSPITAL = 'HOSPITAL',
}

export const CompanyTypeLabels: Record<CompanyTypeEnum, string> = {
  [CompanyTypeEnum.FACTORY]: 'Fábrica',
  [CompanyTypeEnum.GAS_STATION]: 'Posto de Gasolina',
  [CompanyTypeEnum.RESTAURANT]: 'Restaurante',
  [CompanyTypeEnum.OTHER]: 'Outro',
  [CompanyTypeEnum.MARBLE_FACTORY]: 'Fábrica de Mármore',
  [CompanyTypeEnum.HOSPITAL]: 'Hospital',
};

/**
 * Modality type enum for energy contracts
 */
export enum ModalityTypeEnum {
  WHOLESALER = 'WHOLESALER',
  RETAILER = 'RETAILER',
}

export const ModalityTypeLabels: Record<ModalityTypeEnum, string> = {
  [ModalityTypeEnum.WHOLESALER]: 'Atacadista',
  [ModalityTypeEnum.RETAILER]: 'Varejista',
};

// ============================================
// ICMS TYPES
// ============================================

export enum ICMSTypeEnum {
  ICMS_INDUSTRIAL = 'ICMS_INDUSTRIAL',
  ICMS_COMERCIAL = 'ICMS_COMERCIAL',
  ICMS_RURAL = 'ICMS_RURAL',
}

export const ICMSTypeLabels: Record<ICMSTypeEnum, string> = {
  [ICMSTypeEnum.ICMS_INDUSTRIAL]: 'Industrial',
  [ICMSTypeEnum.ICMS_COMERCIAL]: 'Comercial',
  [ICMSTypeEnum.ICMS_RURAL]: 'Rural',
};

// ============================================
// ENERGY TYPES
// ============================================

/**
 * Energy type enum for energy contracts
 * Values are English identifiers matching backend API
 */
export enum EnergyTypeEnum {
  INCENTIVIZED_50 = 'INCENTIVIZED_50',
  INCENTIVIZED_100 = 'INCENTIVIZED_100',
  CONVENTIONAL = 'CONVENTIONAL',
}

export const EnergyTypeLabels: Record<EnergyTypeEnum, string> = {
  [EnergyTypeEnum.INCENTIVIZED_50]: 'Incentivada 50%',
  [EnergyTypeEnum.INCENTIVIZED_100]: 'Incentivada 100%',
  [EnergyTypeEnum.CONVENTIONAL]: 'Convencional',
};

// ============================================
// RISK TYPES
// ============================================

export enum RiskTypeEnum {
  CONSERVATIVE = 'CONSERVATIVE',
  BOLD = 'BOLD',
}

export const RiskTypeLabels: Record<RiskTypeEnum, string> = {
  [RiskTypeEnum.CONSERVATIVE]: 'Conservador',
  [RiskTypeEnum.BOLD]: 'Arrojado',
};

// ============================================
// CONSUMPTION TYPES
// ============================================

export enum ConsumptionTypeEnum {
  LOW_VOLTAGE = 'LOW_VOLTAGE',
  MEDIUM_VOLTAGE_GREEN = 'MEDIUM_VOLTAGE_GREEN',
  MEDIUM_VOLTAGE_BLUE = 'MEDIUM_VOLTAGE_BLUE',
}

export const ConsumptionTypeLabels: Record<ConsumptionTypeEnum, string> = {
  [ConsumptionTypeEnum.LOW_VOLTAGE]: 'Baixa Tensão',
  [ConsumptionTypeEnum.MEDIUM_VOLTAGE_GREEN]: 'Média Tensão Verde',
  [ConsumptionTypeEnum.MEDIUM_VOLTAGE_BLUE]: 'Média Tensão Azul',
};

// ============================================
// MANAGEMENT & GUARANTEE TYPES
// ============================================

export enum ManagementTerminationFineInputEnum {
  HAS_A_FINE = 'HAS_A_FINE',
  NO_FINE = 'NO_FINE',
}

export const ManagementTerminationFineInputLabels: Record<ManagementTerminationFineInputEnum, string> = {
  [ManagementTerminationFineInputEnum.HAS_A_FINE]: 'Possui multa',
  [ManagementTerminationFineInputEnum.NO_FINE]: 'Não possui multa',
};

/**
 * Guarantee type enum for energy contracts
 * Values match backend enum: GuaranteeType in Python
 * Carta fiança

Seguro garantia

Registro contra pagamento

Sem garantia
 */
export enum GuaranteeTypeEnum {
  OTHER = 'OTHER',
  GUARANTEE_LETTER = 'GUARANTEE_LETTER',
  SURETY_BOND = 'SURETY_BOND',
  REGISTRATION_AGAINST_PAYMENT = 'REGISTRATION_AGAINST_PAYMENT',
}

export const GuaranteeTypeLabels: Record<GuaranteeTypeEnum, string> = {
  [GuaranteeTypeEnum.OTHER]: 'Sem Garantia',
  [GuaranteeTypeEnum.GUARANTEE_LETTER]: 'Carta Fiança',
  [GuaranteeTypeEnum.SURETY_BOND]: 'Seguro Garantia',
  [GuaranteeTypeEnum.REGISTRATION_AGAINST_PAYMENT]: 'Registro Contra Pagamento',
};

export enum ManagementTerminationFineEnum {
  THIRTY_PERCENT_OF_THE_CONTRACT_RESIDUAL = 'THIRTY_PERCENT_OF_THE_CONTRACT_RESIDUAL',
  FIFTY_PERCENT_OF_THE_CONTRACT_RESIDUAL = 'FIFTY_PERCENT_OF_THE_CONTRACT_RESIDUAL',
  SEVENTY_PERCENT_OF_THE_CONTRACT_RESIDUAL = 'SEVENTY_PERCENT_OF_THE_CONTRACT_RESIDUAL',
  ONE_HUNDRED_PERCENT_OF_THE_CONTRACT_RESIDUAL = 'ONE_HUNDRED_PERCENT_OF_THE_CONTRACT_RESIDUAL',
  HAS_A_FINE = 'HAS_A_FINE',
  NO_FINE = 'NO_FINE',
}

export const ManagementTerminationFineLabels: Record<ManagementTerminationFineEnum, string> = {
  [ManagementTerminationFineEnum.THIRTY_PERCENT_OF_THE_CONTRACT_RESIDUAL]: '30% do residual do contrato',
  [ManagementTerminationFineEnum.FIFTY_PERCENT_OF_THE_CONTRACT_RESIDUAL]: '50% do residual do contrato',
  [ManagementTerminationFineEnum.SEVENTY_PERCENT_OF_THE_CONTRACT_RESIDUAL]: '70% do residual do contrato',
  [ManagementTerminationFineEnum.ONE_HUNDRED_PERCENT_OF_THE_CONTRACT_RESIDUAL]: '100% do residual do contrato',
  [ManagementTerminationFineEnum.HAS_A_FINE]: 'Possui multa',
  [ManagementTerminationFineEnum.NO_FINE]: 'Não possui multa',
};

export enum ManagerTypeEnum {
  INDEPENDENT_MANAGER = 'INDEPENDENT_MANAGER',
  SALES_MANAGER = 'SALES_MANAGER',
}

export const ManagerTypeLabels: Record<ManagerTypeEnum, string> = {
  [ManagerTypeEnum.INDEPENDENT_MANAGER]: 'Gestora independente',
  [ManagerTypeEnum.SALES_MANAGER]: 'Gestora comercializadora',
};

// ============================================
// FEE & CHARGE TYPES
// ============================================

export enum CustomFeeTypeEnum {
  ABSOLUTE = 'ABSOLUTE',
  PERCENTAGE = 'PERCENTAGE',
}

export const CustomFeeTypeLabels: Record<CustomFeeTypeEnum, string> = {
  [CustomFeeTypeEnum.ABSOLUTE]: 'Em R$/MWh',
  [CustomFeeTypeEnum.PERCENTAGE]: 'Em %',
};

export enum ChargesCoverageTypeForManualAclPricesEnum {
  FULL_COVERAGE_FOR_ENERGY_CONTRACT = 'FULL_COVERAGE_FOR_ENERGY_CONTRACT',
  FULL_PASS_THROUGH_OF_COSTS_TO_THE_CUSTOMER = 'FULL_PASS_THROUGH_OF_COSTS_TO_THE_CUSTOMER',
}

export const ChargesCoverageTypeForManualAclPricesLabels: Record<ChargesCoverageTypeForManualAclPricesEnum, string> = {
  [ChargesCoverageTypeForManualAclPricesEnum.FULL_COVERAGE_FOR_ENERGY_CONTRACT]: 'Cobertura Total (Contr. Energia)',
  [ChargesCoverageTypeForManualAclPricesEnum.FULL_PASS_THROUGH_OF_COSTS_TO_THE_CUSTOMER]: 'Repasse integral ao cliente',
};

// ============================================
// ACL TYPES
// ============================================

export enum AclTypeEnum {
  FIXED_PRICE = 'FIXED_PRICE',
  GUARANTEED_DISCOUNT = 'GUARANTEED_DISCOUNT',
}

export const AclTypeLabels: Record<AclTypeEnum, string> = {
  [AclTypeEnum.FIXED_PRICE]: 'Preço Fixo',
  [AclTypeEnum.GUARANTEED_DISCOUNT]: 'Desconto Garantido',
};
